import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { applicationApi, resumeApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, PageSpinner } from '@/components/ui/Feedback'
import { Kbd } from '@/components/ui/Kbd'
import { Modal } from '@/components/ui/Modal'
import { ProgressBar } from '@/components/ui/Page'
import { ApplicationStatusPill, StatusPill } from '@/components/ui/StatusPill'
import { LivePagePanel } from '@/features/applications/LivePagePanel'
import { PORTAL_LABEL, categoryMeta } from '@/features/mail/mailMeta'
import { NaukriConnectModal } from '@/features/onboarding/NaukriConnectModal'
import { cn, formatSalary, relativeTime } from '@/lib/utils'
import { isLicenseError } from '@/store/license'
import { toast } from '@/store/toast'
import { refreshLicense, useLicense } from '@/features/billing/useLicense'
import type {
  AgentTurn,
  ApplicationDetail,
  GeneratedDocStatus,
  RehearsalAccountGate,
  RehearsalSource,
  RehearsalStep,
  ScoreBreakdown,
  ScreeningQuestion,
  ApplicationScreenshot,
} from '@/types/api'

import { AccountUsedCard } from './AccountUsedCard'
import { AppliedFormPanel, ApplyingPanel } from './AppliedFormPanel'
import { ScreenStateCard } from './ScreenStateCard'
import { ParkedBanner } from './ParkedBanner'
import { CompanyAvatar, STATUS_LABEL } from './ApplicationsTable'
import { BulbIcon, ExternalIcon, ProfileLink, QuestionField, SOURCE_LABEL, Spinner } from './QuestionField'
import { backTarget } from './backTarget'

/** While the worker is driving the portal, the page watches it finish. */
const WORKING = new Set(['queued', 'in_flight'])


const HINT_KEY = 'review-nav-hint-dismissed'

/**
 * One application, full screen.
 *
 * "Needs you" shows only what is missing — everything we filled sits below,
 * collapsed, with who filled it. Submitted applications show the timeline.
 * Prev/next (and the arrow keys) walk the list without going back to it.
 *
 * Layout: a compact sticky header under the app bar; the Status / Resume /
 * Cover letter / Job tabs as a sticky row on phones and a left rail from lg.
 */
export function ApplicationReviewPage() {
  const { applicationId = '' } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const statusFilter = params.get('status')
  const statuses = statusFilter && statusFilter !== 'all' ? [statusFilter] : undefined

  const detailQuery = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => applicationApi.get(applicationId),
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      if (WORKING.has(data.status)) return 2000
      // A tailored resume or cover letter still being written: watch it land.
      return resumeStatus(data) === 'pending' || coverStatus(data) === 'pending' ? 3000 : false
    },
  })
  const [tab, setTab] = useState<SideTab>('form')
  const positionQuery = useQuery({
    queryKey: ['application-position', applicationId, statusFilter],
    queryFn: () => applicationApi.position(applicationId, statuses),
    staleTime: 30_000,
  })

  // Prev / next keep the way back: the screen the first application was opened from.
  const location = useLocation()
  const goTo = (id: string | null | undefined) => {
    if (!id) return
    navigate(`/dashboard/applications/${id}${statusFilter ? `?status=${statusFilter}` : ''}`, { state: location.state })
  }

  const position = positionQuery.data
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (target.closest('input, textarea, select, [contenteditable=true]')) return
      if (event.key === 'ArrowLeft') goTo(position?.previousId)
      if (event.key === 'ArrowRight') goTo(position?.nextId)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.previousId, position?.nextId])

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['application', applicationId] })
    queryClient.invalidateQueries({ queryKey: ['applications'] })
    queryClient.invalidateQueries({ queryKey: ['application-stats'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] })
  }

  if (detailQuery.isLoading) return <PageSpinner label="Loading application" />
  if (detailQuery.isError || !detailQuery.data) {
    return <ErrorState description="We could not load this application." onRetry={() => detailQuery.refetch()} />
  }

  const application = detailQuery.data
  // Always the original posting on the board; the employer's own site, when
  // Naukri redirected there, gets its own link.
  const postingUrl = application.job.applyUrl
  // What stopped the run asks for no answers (a CAPTCHA to solve live, a retry, a wait): the status
  // and why it stopped, with the answers read-only — never the whole form as if it were open
  // questions (live: a CAPTCHA at Submit showed every filled question to "answer").
  const blockerNeeds = application.screen?.blocker?.needs
  const asksForAnswers = !blockerNeeds || blockerNeeds === 'answers' || blockerNeeds === 'upload'

  return (
    <div className="min-w-0">
      <ReviewHeader
        application={application}
        postingUrl={postingUrl}
        prevNext={
          <PrevNext
            index={position?.index}
            total={position?.total}
            onPrev={position?.previousId ? () => goTo(position.previousId) : undefined}
            onNext={position?.nextId ? () => goTo(position.nextId) : undefined}
          />
        }
      />

      <div className="lg:flex lg:items-start lg:gap-8">
        {/* Sticky itself (not just the row inside), so it has the whole page to stick within. */}
        <aside className="sticky top-[calc(var(--safe-top)_+_104px)] z-10 md:top-[calc(var(--safe-top)_+_112px)] lg:top-[calc(var(--safe-top)_+_128px)] lg:w-[248px] lg:shrink-0 xl:w-[272px]">
          <SideTabs application={application} tab={tab} onTab={setTab} />
          <div className="mt-4 hidden lg:block">
            <div className="panel px-4 py-4">
              <JobPanel application={application} />
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 pt-4 lg:pt-0">
          {WORKING.has(application.status) && <LiveSolvePanel application={application} />}
          {application.status === 'needs_you' && <ParkedBanner application={application} />}
          {application.status === 'needs_you' && !application.needsReview && asksForAnswers ? (
            // Waiting on answers or the go-ahead: the form. (A run parked for
            // review has nothing to fill in; it gets the status card and a Retry.)
            // Kept mounted while another tab is open, so typed answers survive.
            <div className={cn('space-y-4', tab !== 'form' && 'hidden')}>
              <StatusPanel application={application} onRetried={refresh} />
              <ScreenStateCard application={application} />
              <ReviewForm key={application.id + application.updatedAt} application={application} onDone={refresh} />
              <AccountUsedCard application={application} />
              <AppliedFormPanel application={application} />
              <RunScreenshots application={application} />
            </div>
          ) : (
            tab === 'form' && (
              <div className="space-y-4">
                {/* While the worker fills the form: what it is doing and the fields in so far. */}
                {WORKING.has(application.status) && !application.isRehearsal && <ApplyingPanel application={application} />}
                {/* Submitted is said once, by the outcome banner. */}
                {application.status !== 'submitted' && !(WORKING.has(application.status) && !application.isRehearsal) && (
                  <StatusPanel application={application} onRetried={refresh} />
                )}
                <OutcomeBanner application={application} />
                {!WORKING.has(application.status) && <ScreenStateCard application={application} />}
                <AccountUsedCard application={application} />
                {!WORKING.has(application.status) && <AppliedFormPanel application={application} />}
                <RunScreenshots application={application} />
                {application.hasRehearsalReport && !(application.isRehearsal && WORKING.has(application.status)) && (
                  <RehearsalPanel application={application} onApplied={refresh} />
                )}
                {application.hasAgentTrace && !WORKING.has(application.status) && (
                  <AgentTracePanel application={application} />
                )}
                <Timeline application={application} />
                <ApplicationEmails application={application} />
                {/* A test run sent nothing: its answers are in the test-run panel. */}
                {!application.isRehearsal && <SentAnswers questions={application.screeningQuestions} />}
              </div>
            )
          )}
          {tab === 'resume' && <ResumeDocument application={application} />}
          {tab === 'cover' && <CoverLetterDocument application={application} />}
          {tab === 'job' && <JobDescriptionDocument application={application} postingUrl={postingUrl} />}
        </main>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
/** The status, the one way it is said anywhere; a test run says so instead. */
function ReviewStatusPill({ application, className }: { application: ApplicationDetail; className?: string }) {
  if (application.isRehearsal && WORKING.has(application.status)) {
    return (
      <StatusPill tone="info" live className={className}>
        Test run
      </StatusPill>
    )
  }
  if (application.isRehearsal && (application.status === 'failed' || application.needsReview)) {
    return (
      <StatusPill tone="neutral" className={className}>
        Test run done
      </StatusPill>
    )
  }
  return <ApplicationStatusPill status={application.status} readyToSubmit={application.readyToSubmit} needsReview={application.needsReview} waitingForYou={application.waitingForYou ?? null} className={className} />
}

/**
 * Compact and sticky under the app bar: back, company and role, the status
 * pill, and prev / next. The posting links show from lg; on phones they sit
 * in the Job view's toolbar instead.
 */
function ReviewHeader({
  application,
  postingUrl,
  prevNext,
}: {
  application: ApplicationDetail
  postingUrl: string
  prevNext: ReactNode
}) {
  // Back to where the application was opened from (Today, the Tracker…), Today when unknown.
  const back = backTarget(useLocation().state)
  const via = application.appliedVia
  // "Naukri" / "Company site (Workday)", and where the job came from when that is somewhere else.
  const portal = via ? via.label : application.portalType ? PORTAL_LABEL[application.portalType] : undefined
  const foundElsewhere = via && via.channel === 'company_site' && via.foundOn.key !== 'direct' ? via.foundOn.label : null
  return (
    <header className="sticky top-[calc(var(--safe-top)_+_56px)] z-20 -mx-4 -mt-5 mb-4 border-b border-line bg-surface/95 backdrop-blur-md sm:-mx-6 sm:-mt-6 lg:-mx-8">
      <div className="flex h-12 items-center gap-1 px-1.5 sm:px-3 md:h-14 md:gap-2 lg:px-5">
        <Link
          to={back.to}
          aria-label={`Back to ${back.label}`}
          title={`Back to ${back.label}`}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-soft transition-colors hover:bg-surface-muted hover:text-ink"
        >
          <BackIcon />
        </Link>
        <span className="hidden md:block">
          <CompanyAvatar name={application.job.companyName} logoUrl={application.job.company?.logoUrl} />
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[13.5px] font-semibold leading-tight tracking-tight text-ink md:text-[15px]">
            {application.job.companyName}
          </h1>
          <p className="truncate text-[12px] leading-tight text-ink-muted md:text-[13px]">{application.job.title}</p>
        </div>
        <ReviewStatusPill application={application} className="max-md:h-5 max-md:px-2 max-md:text-[11px]" />
        {portal && (
          <span
            className="hidden shrink-0 rounded-[6px] border border-line bg-surface-muted px-1.5 py-0.5 text-[11px] font-medium text-ink-soft md:inline"
            title={
              (application.portalAccountEmail
                ? `Applied through ${portal} as ${application.portalAccountEmail}`
                : `Applied through ${portal}`) + (foundElsewhere ? ` — the job was found on ${foundElsewhere}` : '')
            }
          >
            Via {portal}
            {foundElsewhere ? ` · found on ${foundElsewhere}` : ''}
          </span>
        )}
        <a
          href={postingUrl}
          target="_blank"
          rel="noreferrer"
          className="hidden h-9 items-center gap-1.5 rounded-field border border-line bg-surface px-3 text-[13px] font-medium text-ink transition-colors hover:bg-surface-muted lg:inline-flex"
        >
          <ExternalIcon /> View job posting
        </a>
        {application.externalApplyUrl && application.externalApplyUrl !== postingUrl && (
          <a
            href={application.externalApplyUrl}
            target="_blank"
            rel="noreferrer"
            className="hidden h-9 items-center gap-1.5 rounded-field border border-line bg-surface px-3 text-[13px] font-medium text-ink transition-colors hover:bg-surface-muted xl:inline-flex"
          >
            <ExternalIcon /> Employer site
          </a>
        )}
        {prevNext}
      </div>
    </header>
  )
}

function PrevNext({
  index,
  total,
  onPrev,
  onNext,
}: {
  index?: number
  total?: number
  onPrev?: () => void
  onNext?: () => void
}) {
  const [hint, setHint] = useState(() => {
    try {
      return localStorage.getItem(HINT_KEY) !== '1'
    } catch {
      return false
    }
  })
  const dismiss = () => {
    setHint(false)
    try {
      localStorage.setItem(HINT_KEY, '1')
    } catch {
      /* private mode — the hint simply returns next time */
    }
  }

  const arrow =
    'grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink transition-colors hover:bg-surface-muted disabled:text-ink-faint disabled:hover:bg-transparent md:h-9 md:w-9'

  return (
    <div className="relative flex shrink-0 items-center md:ml-1">
      <button type="button" onClick={onPrev} disabled={!onPrev} aria-label="Previous application" title="Previous (←)" className={arrow}>
        <ChevronLeftIcon />
      </button>
      <span className="num hidden min-w-[56px] text-center text-[12px] text-ink-muted md:inline">
        {index && total ? `${index} of ${total}` : '—'}
      </span>
      <button type="button" onClick={onNext} disabled={!onNext} aria-label="Next application" title="Next (→)" className={arrow}>
        <ChevronRightIcon />
      </button>
      <span className="ml-1 hidden lg:block">
        <Kbd keys={['←', '→']} />
      </span>
      {hint && (
        <div className="absolute right-0 top-12 z-20 flex w-max max-w-[min(320px,calc(100vw-32px))] items-center gap-2 rounded-field bg-ink px-3 py-2 text-[12px] text-oncolor shadow-float">
          Flip between applications here, arrow keys work too
          <button type="button" onClick={dismiss} className="shrink-0 whitespace-nowrap rounded-pill bg-oncolor/15 px-2 py-0.5 font-medium">
            Got it
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tabs: Status / Resume / Cover letter / Job
// ---------------------------------------------------------------------------
type SideTab = 'form' | 'resume' | 'cover' | 'job'

function resumeStatus(application: ApplicationDetail): GeneratedDocStatus {
  return application.tailoredResume?.status ?? 'none'
}

function coverStatus(application: ApplicationDetail): GeneratedDocStatus {
  return application.coverLetter?.status ?? 'none'
}

/**
 * A segmented row that sticks under the header on phones; a vertical rail
 * from lg. A pulsing dot on Resume / Cover letter means it is still being
 * written; a steady one means a tailored document is ready.
 */
function SideTabs({
  application,
  tab,
  onTab,
}: {
  application: ApplicationDetail
  tab: SideTab
  onTab: (tab: SideTab) => void
}) {
  const tabs: Array<{ value: SideTab; label: string; dot?: 'pending' | 'ready' }> = [
    { value: 'form', label: 'Status' },
    { value: 'resume', label: 'Resume', dot: docDot(resumeStatus(application)) },
    { value: 'cover', label: 'Cover letter', dot: docDot(coverStatus(application)) },
    { value: 'job', label: 'Job' },
  ]
  return (
    <div className="-mx-4 bg-canvas/95 px-4 py-2 backdrop-blur-md sm:-mx-6 sm:px-6 lg:mx-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
      <div
        role="tablist"
        aria-label="Application details"
        className="grid grid-cols-4 gap-0.5 rounded-[10px] border border-line bg-surface-muted p-0.5 lg:flex lg:flex-col lg:gap-0.5 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0"
      >
        {tabs.map((item) => {
          const selected = tab === item.value
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onTab(item.value)}
              className={cn(
                'relative flex h-10 min-w-0 items-center justify-center gap-2 rounded-[8px] px-1 text-[12.5px] font-medium transition-colors sm:text-[13px]',
                'lg:h-10 lg:justify-start lg:rounded-field lg:px-3 lg:text-[14px]',
                selected
                  ? 'bg-ink text-oncolor shadow-sm lg:bg-accent-soft lg:text-ink lg:shadow-none'
                  : 'text-ink-muted hover:text-ink lg:hover:bg-surface-muted',
              )}
            >
              {selected && <span aria-hidden="true" className="absolute -left-3 top-2 hidden h-6 w-[3px] rounded-r bg-brand lg:block" />}
              <span className="truncate">{item.label}</span>
              {item.dot && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full lg:static lg:ml-auto',
                    item.dot === 'pending' ? 'animate-pulse bg-info' : 'bg-brand',
                  )}
                />
              )}
              {item.dot && <span className="sr-only">{item.dot === 'pending' ? ' (being written)' : ' (ready)'}</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function docDot(status: GeneratedDocStatus): 'pending' | 'ready' | undefined {
  if (status === 'pending') return 'pending'
  if (status === 'ready') return 'ready'
  return undefined
}

/** One line on what went with the application: the resume. */
function ResumePanel({ application }: { application: ApplicationDetail }) {
  const status = resumeStatus(application)
  const line = {
    ready: 'A resume tailored to this job description.',
    pending: 'Your resume is being tailored to this job.',
    failed: 'Your master resume. Tailoring did not work out for this one.',
    none: 'Your master resume, as you uploaded it.',
  }[status]
  return <p className="text-[13px] text-ink-muted">{line}</p>
}

function CoverPanel({ application }: { application: ApplicationDetail }) {
  const status = coverStatus(application)
  const line =
    status === 'ready'
      ? `A cover letter written for ${application.job.companyName}.`
      : status === 'pending'
        ? 'Your cover letter is being written.'
        : 'No cover letter went with this application.'
  return <p className="text-[13px] text-ink-muted">{line}</p>
}

const BREAKDOWN: Array<{ key: keyof ScoreBreakdown; label: string; max: number }> = [
  { key: 'skills', label: 'Skills', max: 30 },
  { key: 'title', label: 'Title', max: 15 },
  { key: 'experience', label: 'Experience', max: 15 },
  { key: 'description', label: 'Job description fit', max: 15 },
  { key: 'location', label: 'Location', max: 10 },
  { key: 'workAuthorization', label: 'Work authorization', max: 10 },
  { key: 'salary', label: 'Salary', max: 5 },
]

/** The job in brief: facts, match breakdown, skills. The rail on lg; the Job view on phones. */
function JobPanel({ application }: { application: ApplicationDetail }) {
  const { job } = application
  const salary = formatSalary(job.minSalary, job.maxSalary, job.salaryCurrency)
  const facts = [
    job.location,
    job.minExperienceYears != null ? `${job.minExperienceYears}–${job.maxExperienceYears ?? '+'} yrs` : null,
    salary,
    job.workplaceType,
  ].filter(Boolean)

  return (
    <div className="space-y-4 text-[14px]">
      <div>
        <p className="font-medium text-ink">{job.title}</p>
        <p className="text-ink-muted">{job.companyName}</p>
        {facts.length > 0 && <p className="mt-1 text-[13px] text-ink-muted">{facts.join(' · ')}</p>}
      </div>

      {application.scoreBreakdown && (
        <section>
          <p className="label-eyebrow mb-2">
            Match <span className="num">{application.matchScore ?? 0}%</span>
          </p>
          <ul className="space-y-1.5">
            {BREAKDOWN.map(({ key, label, max }) => {
              const value = application.scoreBreakdown?.[key] ?? 0
              return (
                <li key={key} className="text-[12px]">
                  <div className="flex justify-between text-ink-soft">
                    <span>{label}</span>
                    <span className="num">
                      {value}/{max}
                    </span>
                  </div>
                  <div className="mt-0.5 h-1.5 rounded-full bg-ink/[0.07]">
                    <div className="h-1.5 rounded-full bg-ink" style={{ width: `${(value / max) * 100}%` }} />
                  </div>
                </li>
              )
            })}
          </ul>
          {(application.scoreBreakdown.fitPenalty ?? 0) < 0 && <p className="mt-3 text-[12px] leading-relaxed text-warning">Experience-level adjustment: {application.scoreBreakdown.fitPenalty} points. This role differs substantially from your experience level.</p>}
        </section>
      )}

      {(application.matchedSkills.length > 0 || application.missingSkills.length > 0) && (
        <section className="space-y-2">
          <p className="label-eyebrow">Skills</p>
          <div className="flex flex-wrap gap-1.5">
            {application.matchedSkills.map((skill) => (
              <span key={skill} className="rounded-[6px] bg-success/10 px-1.5 py-0.5 text-[12px] text-success">
                {skill}
              </span>
            ))}
            {application.missingSkills.map((skill) => (
              <span key={skill} className="rounded-[6px] bg-surface-muted px-1.5 py-0.5 text-[12px] text-ink-muted">
                {skill}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Collapsible section
// ---------------------------------------------------------------------------
/**
 * A card that folds: a summary row (title, count, a short meta) and the body
 * under it. Keeps a phone from becoming one long wall. Uncontrolled unless
 * `open` is given.
 */
function Disclosure({
  title,
  count,
  meta,
  defaultOpen = false,
  open,
  onToggle,
  children,
  className,
}: {
  title: string
  count?: number
  meta?: ReactNode
  defaultOpen?: boolean
  open?: boolean
  onToggle?: (open: boolean) => void
  children: ReactNode
  className?: string
}) {
  const [internal, setInternal] = useState(defaultOpen)
  const isOpen = open ?? internal
  const toggle = () => (onToggle ? onToggle(!isOpen) : setInternal(!isOpen))
  return (
    <section className={cn('card overflow-hidden', className)}>
      <h2 className="m-0">
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={toggle}
          className="flex min-h-[48px] w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-surface-muted/60 sm:px-5"
        >
          <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
            <span className="text-[14.5px] font-semibold text-ink">{title}</span>
            {count != null && <span className="num text-[12.5px] text-ink-muted">{count}</span>}
          </span>
          {meta && <span className="hidden min-w-0 truncate text-[12px] text-ink-faint sm:inline">{meta}</span>}
          <span className="text-ink-muted">
            <ChevronIcon open={isOpen} />
          </span>
        </button>
      </h2>
      {isOpen && <div className="border-t border-line">{children}</div>}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Documents: what was sent (resume, cover letter) and the full posting
// ---------------------------------------------------------------------------
function DocShell({ children }: { children: ReactNode }) {
  return <div className="mx-auto w-full max-w-[880px] space-y-4">{children}</div>
}

/** One small toolbar row: title and a line about it on the left, actions on the right. */
function DocToolbar({ title, subtitle, action }: { title: string; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h2 className="truncate text-[16px] font-semibold tracking-tight text-ink">{title}</h2>
        {subtitle && <div className="mt-0.5 truncate text-[13px] text-ink-muted">{subtitle}</div>}
      </div>
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  )
}

/** A sheet on the canvas, so a document reads as the document. */
function Paper({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('mx-auto w-full max-w-[816px] overflow-hidden rounded-[6px] bg-surface shadow-card ring-1 ring-line', className)}>
      {children}
    </div>
  )
}

function PendingDoc({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-center rounded-card border border-line bg-surface-muted/50 px-6 py-16 text-center">
      <span className="grid h-10 w-10 place-items-center rounded-full bg-info/10 text-info">
        <Spinner />
      </span>
      <p className="mt-4 text-[15px] font-medium text-ink" role="status">
        {title}
      </p>
      <p className="mt-1 max-w-sm text-[13px] leading-relaxed text-ink-muted">{body}</p>
    </div>
  )
}

function MasterResumeButton({ application }: { application: ApplicationDetail }) {
  if (!application.resumeId) {
    return <p className="text-[13px] text-ink-muted">No resume on file. Upload one in your profile.</p>
  }
  return (
    <Button
      size="sm"
      variant="secondary"
      icon={<FileIcon />}
      onClick={() =>
        resumeApi
          .download(application.resumeId!, application.resumeFileName ?? 'resume')
          .catch((error: ApiError) => toast.error('Download failed', error.message))
      }
      title={application.resumeFileName ?? 'Master resume'}
      className="max-w-[60vw] sm:max-w-none"
    >
      <span className="min-w-0 truncate">{application.resumeFileName ?? 'Master resume'}</span>
      <span className="shrink-0 text-ink-muted">· Download</span>
    </Button>
  )
}

function NaukriProfileNote({ tailored }: { tailored: boolean }) {
  return (
    <p className="flex gap-2 rounded-field border border-line bg-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-ink-muted">
      <LockIcon />
      <span>
        {tailored ? (
          <>
            Naukri quick apply sends the resume on your <strong className="font-medium text-ink">Naukri profile</strong>,
            so this one was uploaded there just for this application and yours was put back after.
          </>
        ) : (
          <>
            Naukri quick apply sends the resume on your <strong className="font-medium text-ink">Naukri profile</strong>.
            Keep it current on Naukri for these applications.
          </>
        )}
      </span>
    </p>
  )
}

function ResumeDocument({ application }: { application: ApplicationDetail }) {
  const tailored = application.tailoredResume
  const status = resumeStatus(application)
  const easy = application.applyMode === 'easy_apply'
  const [downloading, setDownloading] = useState(false)

  const downloadTailored = () => {
    setDownloading(true)
    applicationApi
      .downloadTailoredResume(
        application.id,
        tailored?.fileName ?? `Resume - ${application.job.companyName}.pdf`,
      )
      .catch((error: ApiError) => toast.error('Download failed', error.message))
      .finally(() => setDownloading(false))
  }

  if (status === 'pending') {
    return (
      <DocShell>
        <PendingDoc
          title="Tailoring your resume for this job…"
          body="We're matching your summary, highlights and bullet order to the job description. This page updates on its own."
        />
      </DocShell>
    )
  }

  if (status === 'ready') {
    return (
      <DocShell>
        <DocToolbar
          title="Resume — tailored for this job"
          subtitle={tailored?.fileName ?? <ResumePanel application={application} />}
          action={
            <Button size="sm" variant="secondary" icon={<DownloadIcon />} onClick={downloadTailored} loading={downloading}>
              Download PDF
            </Button>
          }
        />
        {tailored?.html ? (
          <Paper>
            {/* The document paints its own white page, so it reads as paper in both themes. */}
            <iframe
              title="Tailored resume"
              sandbox=""
              srcDoc={tailored.html}
              className="block h-[min(1056px,78vh)] min-h-[480px] w-full bg-surface"
            />
          </Paper>
        ) : (
          <p className="rounded-field bg-surface-muted px-3.5 py-3 text-[13px] text-ink-muted">
            The preview isn't available, but the PDF that was sent can still be downloaded.
          </p>
        )}
        {easy && <NaukriProfileNote tailored />}
      </DocShell>
    )
  }

  return (
    <DocShell>
      <DocToolbar
        title={status === 'failed' ? 'Your master resume was sent' : 'Your master resume was sent'}
        subtitle={status === 'failed' ? <ResumePanel application={application} /> : 'The resume you uploaded, unchanged.'}
        action={<MasterResumeButton application={application} />}
      />
      {status === 'failed' && (
        <div className="flex gap-3 rounded-card border border-warning/25 bg-warning/[0.06] px-4 py-4 sm:px-5">
          <InfoIcon />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-ink">
              We couldn't tailor this one, so your master resume was sent
            </p>
            {tailored?.error && (
              <p className="mt-1 break-words text-[12.5px] leading-relaxed text-ink-muted">{tailored.error}</p>
            )}
          </div>
        </div>
      )}
      {easy && <NaukriProfileNote tailored={false} />}
    </DocShell>
  )
}

function CoverLetterDocument({ application }: { application: ApplicationDetail }) {
  const status = coverStatus(application)
  const text = application.coverLetter?.text?.trim() ?? ''

  if (status === 'pending') {
    return (
      <DocShell>
        <PendingDoc
          title="Writing your cover letter…"
          body="A short letter for this role, from your profile and the job description. This page updates on its own."
        />
      </DocShell>
    )
  }

  if (status !== 'ready' || !text) {
    return (
      <DocShell>
        <DocToolbar title="Cover letter" subtitle={<CoverPanel application={application} />} />
        <EmptyState
          icon={
            <span className="grid h-12 w-12 place-items-center rounded-full bg-surface-muted">
              <LetterIcon />
            </span>
          }
          title="No cover letter was sent"
          description="This application didn't include a cover letter."
        />
      </DocShell>
    )
  }

  const copy = () =>
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success('Cover letter copied'))
      .catch(() => toast.error('Could not copy', 'Select the text and copy it instead.'))

  return (
    <DocShell>
      <DocToolbar
        title="Cover letter"
        subtitle={`${application.job.title} at ${application.job.companyName}`}
        action={
          <Button size="sm" variant="secondary" icon={<CopyIcon />} onClick={copy}>
            Copy
          </Button>
        }
      />
      <Paper className="px-4 py-6 sm:px-14 sm:py-14">
        <div className="mx-auto max-w-[600px] space-y-4 text-[15px] leading-[1.75] text-ink">
          {text.split(/\n\s*\n/).map((paragraph, index) => (
            <p key={index} className="whitespace-pre-line">
              {paragraph.trim()}
            </p>
          ))}
        </div>
      </Paper>
    </DocShell>
  )
}

const HTML_HINT = /<\/?(p|div|ul|ol|li|br|h[1-6]|strong|b|em|i|span|table|section)\b[^>]*>/i

/** A theme token as the frame can use it: the app's CSS variables do not cross into an iframe. */
function tokenColor(name: string, fallback: string) {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value ? `rgb(${value})` : fallback
}

/** Re-renders when the theme attribute on <html> changes, so framed documents follow it. */
function useThemeVersion() {
  const [version, setVersion] = useState(0)
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return
    const observer = new MutationObserver(() => setVersion((v) => v + 1))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])
  return version
}

/** Plain type for the posting's own HTML, in the app's current colours. */
function jdStyle() {
  const ink = tokenColor('--ink', 'CanvasText')
  const soft = tokenColor('--ink-soft', 'CanvasText')
  const line = tokenColor('--line', 'GrayText')
  const link = tokenColor('--info', 'LinkText')
  // The frame must share the page's colour scheme, or the browser paints an
  // opaque backdrop behind it instead of letting the card show through.
  const scheme = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
  return `
  html { color-scheme: ${scheme}; }
  html, body { margin: 0; padding: 0; background: transparent; }
  body { font: 14px/1.65 Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
         color: ${soft}; overflow-wrap: anywhere; }
  h1, h2, h3, h4, h5, h6 { color: ${ink}; font-weight: 600; line-height: 1.35; margin: 1.3em 0 .5em; }
  h1 { font-size: 18px; } h2 { font-size: 16px; } h3, h4, h5, h6 { font-size: 14.5px; }
  p { margin: 0 0 .85em; }
  ul, ol { margin: 0 0 .85em; padding-left: 1.3em; }
  li { margin: .2em 0; }
  strong, b { color: ${ink}; font-weight: 600; }
  a { color: ${link}; }
  table { border-collapse: collapse; max-width: 100%; }
  td, th { border: 1px solid ${line}; padding: 4px 8px; text-align: left; }
  img { max-width: 100%; height: auto; }
  body > :first-child { margin-top: 0; }
  body > :last-child { margin-bottom: 0; }
  body { display: flow-root; }
`
}

/**
 * The posting's HTML in a sandbox: no scripts run. Same-origin (still no
 * scripts) only so the frame can be sized to its content, not scrolled;
 * links open in a new tab.
 */
function HtmlDescription({ html }: { html: string }) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(240)
  useThemeVersion()

  const measure = () => {
    const doc = frameRef.current?.contentDocument
    // The body, not the document: the document never reports less than the frame.
    if (doc?.body) setHeight(Math.ceil(doc.body.getBoundingClientRect().height) + 4)
  }

  useEffect(() => {
    const frame = frameRef.current
    if (!frame || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measure())
    observer.observe(frame)
    return () => observer.disconnect()
  }, [])

  return (
    <iframe
      ref={frameRef}
      title="Job description"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>${jdStyle()}</style></head><body>${html}</body></html>`}
      onLoad={measure}
      style={{ height }}
      className="block w-full"
    />
  )
}

function JobDescriptionDocument({ application, postingUrl }: { application: ApplicationDetail; postingUrl: string }) {
  const description = (application.jobDescription ?? application.job.description ?? '').trim()
  return (
    <DocShell>
      <DocToolbar
        title="Job description"
        subtitle={`${application.job.title} · ${application.job.companyName}`}
        action={
          <>
            <a
              href={postingUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 items-center gap-1.5 rounded-field border border-line bg-surface px-3 text-[13px] font-medium text-ink transition-colors hover:bg-surface-muted"
            >
              <ExternalIcon /> <span className="hidden xs:inline">View job </span>posting
            </a>
            {application.externalApplyUrl && application.externalApplyUrl !== postingUrl && (
              <a
                href={application.externalApplyUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center gap-1.5 rounded-field border border-line bg-surface px-3 text-[13px] font-medium text-ink transition-colors hover:bg-surface-muted"
              >
                <ExternalIcon /> Employer site
              </a>
            )}
          </>
        }
      />
      <div className="panel px-4 py-4 lg:hidden">
        <JobPanel application={application} />
      </div>
      {!description ? (
        <EmptyState title="No description saved" description="Open the job posting to read the full description." />
      ) : (
        <Paper className="px-4 py-5 sm:px-8 sm:py-8">
          {HTML_HINT.test(description) ? (
            <HtmlDescription html={description} />
          ) : (
            <div className="whitespace-pre-line break-words text-[14px] leading-[1.65] text-ink-soft">{description}</div>
          )}
        </Paper>
      )}
    </DocShell>
  )
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------
/** What is happening with this application, in one compact card, with the actions that fit it. */
function StatusPanel({ application, onRetried }: { application: ApplicationDetail; onRetried: () => void }) {
  const [connectOpen, setConnectOpen] = useState(false)
  // Retrying sends the application: no plan (or none left this month) → the upgrade dialog.
  const { requireApply } = useLicense()
  const queryClient = useQueryClient()
  // The apply runner fails with one of these when the Naukri session is missing,
  // expired, or was minted in demo mode; fixing it means signing in again.
  // A run that stopped: parked for review with its reason (older rows were stored as "failed").
  const stopped = application.status === 'failed' || application.needsReview
  const needsNaukriLogin = stopped && /reconnect naukri|connect your naukri|naukri session has expired/i.test(application.failureReason ?? '')

  const retry = useMutation({
    mutationFn: () => applicationApi.retry(application.id),
    onSuccess: () => {
      toast.info(application.isRehearsal ? 'Applying for real' : 'Trying again')
      refreshLicense(queryClient)
      onRetried()
    },
    onError: (error: ApiError) => {
      if (!isLicenseError(error)) toast.error('Could not retry', error.message)
    },
  })
  const rehearse = useMutation({
    mutationFn: () => applicationApi.rehearse(application.id),
    onSuccess: () => {
      toast.info('Test run started', 'We will stop before Submit — nothing is sent')
      onRetried()
    },
    onError: (error: ApiError) => toast.error('Could not start a test run', error.message),
  })
  // A test run walks the employer's own form; Naukri quick apply has none.
  const companyPortal = Boolean(application.portalType) || application.applyMode === 'company_portal'
  const canRehearse = companyPortal && (application.status === 'failed' || application.status === 'needs_you')
  const rehearsalDone = application.isRehearsal && stopped
  const testRunButton = canRehearse && (
    <Button size="sm" variant="secondary" onClick={() => rehearse.mutate()} loading={rehearse.isPending}>
      Test run (no submit)
    </Button>
  )

  const via =
    application.applyMode === 'company_portal'
      ? `${application.job.companyName}'s careers site`
      : application.job.source === 'naukri'
        ? 'Naukri'
        : 'the employer'

  const panel = (() => {
    if (application.isRehearsal && WORKING.has(application.status)) {
      return {
        icon: <Spinner />,
        tone: 'bg-info/10 text-info',
        title: 'Test run in progress…',
        subtitle: application.status === 'queued' ? 'In the queue' : `Walking the form on ${via}`,
        body: 'We fill every step and stop before Submit, account sign-up or sign-in. Nothing is sent.',
      }
    }
    if (rehearsalDone) {
      return {
        icon: <InfoIcon />,
        tone: 'bg-info/10 text-info',
        title: 'Test run finished',
        subtitle: `On ${via}`,
        body: application.failureReason ?? 'The test run finished. Nothing was sent.',
      }
    }
    switch (application.status) {
      case 'queued':
      case 'in_flight':
        return {
          icon: <Spinner />,
          tone: 'bg-info/10 text-info',
          title: 'Applying…',
          subtitle: application.status === 'queued' ? 'In the queue' : `Filling the form on ${via}`,
          body: 'This usually takes under a minute. You can leave this page — it keeps going.',
        }
      case 'needs_you':
        if (application.needsReview) {
          return {
            icon: <AlertIcon />,
            tone: 'bg-warning/12 text-warning',
            title: 'Needs your review',
            subtitle: `Stopped on ${via}. Nothing was sent.`,
            body: application.failureReason ?? 'The run stopped before it could finish. Retry runs it again from the start.',
          }
        }
        return application.pendingRequiredCount > 0
          ? {
              icon: <PencilIcon />,
              tone: 'bg-warning/15 text-warning',
              title: 'Action required',
              subtitle: 'Waiting for your answers',
              body: (
                <>
                  {via === 'Naukri' ? 'Naukri' : via} asked a few things your profile does not cover
                  for <strong className="font-medium text-ink">{application.job.companyName}</strong>.
                  Answer them once and we will remember.
                </>
              ),
            }
          : {
              icon: <PencilIcon />,
              tone: 'bg-warning/15 text-warning',
              title: 'Review before submit',
              subtitle: 'Waiting for your approval',
              body: (
                <>
                  Check what we filled in for{' '}
                  <strong className="font-medium text-ink">{application.job.companyName}</strong>,
                  and edit anything before we submit.
                </>
              ),
            }
      case 'submitted':
        return {
          icon: <CheckIcon />,
          tone: 'bg-success/12 text-success',
          title: 'Submitted',
          subtitle: relativeTime(application.submittedAt),
          body: `Sent through ${via}.`,
        }
      case 'failed':
        return {
          icon: <AlertIcon />,
          tone: 'bg-danger/10 text-danger',
          title: 'Could not finish',
          subtitle: `Attempted on ${via}`,
          body: application.failureReason ?? 'The portal did not accept the application.',
        }
      default:
        return {
          icon: <AlertIcon />,
          tone: 'bg-surface-muted text-ink-muted',
          title: STATUS_LABEL[application.status],
          subtitle: relativeTime(application.updatedAt),
          body: null,
        }
    }
  })()

  return (
    <section className="card px-4 py-3.5 sm:px-5 sm:py-4">
      <div className="flex items-start gap-3">
        <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-[10px]', panel.tone)}>{panel.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <p className="text-[15px] font-semibold text-ink">{panel.title}</p>
            <p className="text-[12.5px] text-ink-muted">{panel.subtitle}</p>
          </div>
          {panel.body && <p className="mt-1 text-[13.5px] leading-relaxed text-ink-soft">{panel.body}</p>}

          {rehearsalDone && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => requireApply(() => retry.mutate())} loading={retry.isPending}>
                Apply for real
              </Button>
              {testRunButton}
            </div>
          )}

          {stopped && !rehearsalDone && (
            <div className="mt-3 flex flex-wrap gap-2">
              {needsNaukriLogin && (
                <Button size="sm" onClick={() => setConnectOpen(true)}>
                  Reconnect Naukri
                </Button>
              )}
              <Button
                size="sm"
                variant={needsNaukriLogin ? 'secondary' : 'primary'}
                onClick={() => requireApply(() => retry.mutate())}
                loading={retry.isPending}
              >
                Retry
              </Button>
              {testRunButton}
              {application.externalApplyUrl && (
                <a
                  href={application.externalApplyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-9 items-center rounded-field border border-line px-3.5 text-[13px] text-ink hover:bg-surface-muted"
                >
                  Apply on their site
                </a>
              )}
            </div>
          )}

          {application.status === 'needs_you' && !stopped && canRehearse && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {testRunButton}
              <span className="text-[12px] text-ink-muted">Walks {via} without submitting</span>
            </div>
          )}

          {application.status === 'needs_you' && !stopped && (
            <p className="mt-3 flex gap-2 text-[12px] leading-relaxed text-ink-muted">
              <LockIcon />
              <span>
                Answers marked Profile come straight from your saved profile. Change them there to update every
                future application; anything edited here applies to this one.
              </span>
            </p>
          )}
        </div>
      </div>
      <NaukriConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        reconnect
        // Once signed in, pick the application straight back up.
        onConnected={() => requireApply(() => retry.mutate())}
      />
    </section>
  )
}

// ---------------------------------------------------------------------------
// Unreadable question
// ---------------------------------------------------------------------------
function UnreadableQuestion({ application, onDone }: { application: ApplicationDetail; onDone: () => void }) {
  const { requireApply } = useLicense()
  const retry = useMutation({
    mutationFn: () => applicationApi.retry(application.id),
    onSuccess: () => {
      toast.info('Trying again', `Reopening the application on ${application.job.companyName}`)
      onDone()
    },
    onError: (error: ApiError) => {
      if (!isLicenseError(error)) toast.error('Could not retry', error.message)
    },
  })

  return (
    <section className="card p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <InfoIcon />
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-ink">We could not read Naukri's question</p>
          <p className="mt-1 text-[13.5px] leading-relaxed text-ink-muted">
            Naukri's apply chat asked something, but the text had not loaded when we read it, so there is
            nothing to answer here. Try again and we will reopen the application and read the chat again,
            or finish it yourself on Naukri.
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 sm:pl-8">
        <Button size="sm" onClick={() => requireApply(() => retry.mutate())} loading={retry.isPending}>
          Try again
        </Button>
        <a
          href={application.job.applyUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-9 items-center rounded-field border border-line px-3.5 text-[13px] text-ink hover:bg-surface-muted"
        >
          Open on Naukri
        </a>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Review form ("N details needed")
// ---------------------------------------------------------------------------
function ReviewForm({ application, onDone }: { application: ApplicationDetail; onDone: () => void }) {
  const navigate = useNavigate()
  const back = backTarget(useLocation().state)
  const questions = application.screeningQuestions
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(questions.map((q) => [q.questionKey, q.answerValue ?? ''])),
  )
  const [showFilled, setShowFilled] = useState(application.pendingRequiredCount === 0)
  const [showOptional, setShowOptional] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { requireApply } = useLicense()
  const queryClient = useQueryClient()

  // "Missing" is fixed at load: a question stays in the top card while typed
  // into, instead of jumping down to the filled list mid-sentence. Only a
  // required question is a detail needed; an unanswered optional one waits,
  // collapsed, under "Optional".
  const missing = useMemo(() => questions.filter((q) => q.isRequired && !q.answerValue), [questions])
  const optional = useMemo(
    () => questions.filter((q) => !q.isRequired && !q.answerValue && !leftEmptyOnPurpose(q)),
    [questions],
  )
  const filled = useMemo(() => questions.filter((q) => q.answerValue), [questions])
  const outstanding = questions.filter((q) => q.isRequired && !answers[q.questionKey]?.trim()).length

  const set = (key: string, value: string) => setAnswers((current) => ({ ...current, [key]: value }))

  const submit = useMutation({
    mutationFn: () =>
      applicationApi.submitScreening(
        application.id,
        questions.map((q) => ({ questionKey: q.questionKey, answerValue: answers[q.questionKey] || null })),
        true,
      ),
    onSuccess: () => {
      toast.success('Submitting', `Sending your application to ${application.job.companyName}`)
      refreshLicense(queryClient)
      onDone()
    },
    onError: (caught: ApiError) => {
      // A 402 opens the upgrade dialog; the form keeps the answers for after.
      if (!isLicenseError(caught)) setError(caught.message)
    },
  })

  const cancel = useMutation({
    mutationFn: () => applicationApi.cancel(application.id),
    onSuccess: () => {
      toast.info('Application cancelled')
      onDone()
      navigate(back.to)
    },
    onError: (caught: ApiError) => toast.error('Could not cancel', caught.message),
  })

  const answeredMissing = missing.filter((q) => answers[q.questionKey]?.trim()).length

  // Why the portal handed this back, when it was not for missing answers.
  const portalAsk = useMemo(() => {
    const last = [...(application.events ?? [])]
      .reverse()
      .find((event) => event.toStatus === 'needs_you')
    const message = last?.message?.trim()
    if (!message || /details? needed|ready for your review/i.test(message)) return null
    return message
  }, [application.events])
  // Profile tips for the questions the user had to answer by hand: fill these
  // in once and the same questions stop landing here.
  const tips = useMemo(
    () =>
      [...new Set(missing.map((q) => q.tip?.trim()).filter((tip): tip is string => Boolean(tip)))].slice(0, 4),
    [missing],
  )

  // A question captured with no text cannot be answered here; offer a fresh
  // attempt instead of a blank required field that blocks Submit forever.
  if (questions.some((q) => !q.questionText.trim())) {
    return <UnreadableQuestion application={application} onDone={onDone} />
  }

  return (
    <div className="space-y-4">
      {missing.length > 0 ? (
        <>
          <section className="card px-4 py-3.5 sm:px-5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
              <p className="text-[15px] font-semibold text-ink">
                <span className="num">{missing.length}</span> detail{missing.length === 1 ? '' : 's'} needed
              </p>
              <p className="num text-[12px] font-medium text-ink-muted">
                {answeredMissing}/{missing.length} answered
              </p>
            </div>
            <p className="mt-0.5 text-[13px] text-ink-muted">
              Add only the missing information below. Everything else is already filled.
            </p>
            <ProgressBar
              className="mt-3"
              value={(answeredMissing / missing.length) * 100}
              label="Details answered"
              tone={answeredMissing === missing.length ? 'brand' : 'warning'}
            />
          </section>
          <ol className="space-y-3">
            {missing.map((question, index) => {
              const done = Boolean(answers[question.questionKey]?.trim())
              return (
                <li key={question.questionKey} className="card flex gap-3 p-4 sm:p-5">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'num mt-px grid h-6 w-6 shrink-0 place-items-center rounded-full text-[12px] font-semibold',
                      done ? 'bg-brand text-oncolor' : 'bg-surface-muted text-ink-muted',
                    )}
                  >
                    {done ? <TickIcon /> : index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <QuestionField
                      applicationId={application.id}
                      question={question}
                      value={answers[question.questionKey] ?? ''}
                      onChange={(value) => set(question.questionKey, value)}
                    />
                  </div>
                </li>
              )
            })}
          </ol>
          {tips.length > 0 && <SaveTimePanel tips={tips} />}
        </>
      ) : portalAsk ? (
        // The portal stopped for something that is not a question: no
        // application password yet, or an account email still unverified.
        <section className="flex items-start gap-3 rounded-card border border-warning/30 bg-warning/[0.06] px-4 py-4 sm:px-5">
          <span className="mt-0.5">
            <InfoIcon />
          </span>
          <div>
            <p className="text-[15px] font-semibold text-ink">One thing to do first</p>
            <p className="mt-0.5 text-[13.5px] leading-relaxed text-ink-soft">{portalAsk}</p>
            <p className="mt-2 text-[13px] text-ink-muted">
              {/password/i.test(portalAsk) ? (
                <>
                  <a href="/dashboard/settings#application-password" target="_blank" rel="noreferrer" className="font-medium text-brand hover:underline">
                    Open Settings
                  </a>{' '}
                  to set it, then submit below and we will pick the application back up.
                </>
              ) : (
                'When that is done, submit below and we will pick the application back up.'
              )}
            </p>
          </div>
        </section>
      ) : (
        <section className="flex items-start gap-3 rounded-card border border-brand/25 bg-brand-soft px-4 py-4 sm:px-5">
          <span className="mt-0.5 text-brand">
            <CheckIcon />
          </span>
          <div>
            <p className="text-[15px] font-semibold text-ink">Everything is filled</p>
            <p className="text-[13px] text-ink-muted">Look over the answers below, then submit.</p>
          </div>
        </section>
      )}

      {optional.length > 0 && (
        <Disclosure title="Optional" count={optional.length} open={showOptional} onToggle={setShowOptional}>
          <div className="divide-y divide-line">
            {optional.map((question) => (
              <div key={question.questionKey} className="px-4 py-4 sm:px-5">
                <QuestionField
                  applicationId={application.id}
                  question={question}
                  value={answers[question.questionKey] ?? ''}
                  onChange={(value) => set(question.questionKey, value)}
                />
              </div>
            ))}
          </div>
        </Disclosure>
      )}

      {filled.length > 0 && (
        <Disclosure title="Already filled" count={filled.length} open={showFilled} onToggle={setShowFilled}>
          <div className="divide-y divide-line">
            {filled.map((question) => (
              <div key={question.questionKey} className="px-4 py-4 sm:px-5">
                <QuestionField
                  applicationId={application.id}
                  question={question}
                  value={answers[question.questionKey] ?? ''}
                  onChange={(value) => set(question.questionKey, value)}
                  showSource
                />
              </div>
            ))}
          </div>
        </Disclosure>
      )}

      {error && (
        <p role="alert" className="rounded-field bg-danger/8 px-3.5 py-2.5 text-[14px] text-danger">
          {error}
        </p>
      )}

      {/* Sticky above the tab bar on phones; the end of the form on desktop. */}
      <div className="action-bar flex items-center justify-between gap-2 md:static md:mt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => cancel.mutate()}
          disabled={submit.isPending || cancel.isPending}
          className="shrink-0 px-1.5 sm:px-3"
        >
          <span className="sm:hidden">Cancel</span>
          <span className="hidden sm:inline">Cancel application</span>
        </Button>
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          {outstanding > 0 && (
            <p className="whitespace-nowrap text-[12px] text-ink-muted sm:text-[12.5px]" aria-live="polite">
              <span className="num font-semibold text-warning">{outstanding}</span> required left
            </p>
          )}
          <Button
            icon={<SendIcon />}
            onClick={() => requireApply(() => submit.mutate())}
            disabled={outstanding > 0}
            loading={submit.isPending}
            className="shrink-0 whitespace-nowrap px-4 text-[14px] sm:px-5 sm:text-[15px]"
          >
            Submit application
          </Button>
        </div>
      </div>
    </div>
  )
}

/** An optional field the rules leave blank by design (a phone extension, a
 *  name in the local script): nothing for the user to decide, so not shown. */
const leftEmptyOnPurpose = (question: ScreeningQuestion) =>
  !question.isRequired && !question.answerValue && question.rationale === 'Left empty on purpose'




/** Sits under the questions: the profile gaps that put them here. */
function SaveTimePanel({ tips }: { tips: string[] }) {
  return (
    <div className="panel bg-surface-muted/40 px-4 py-4 sm:px-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-[13.5px] font-semibold text-ink">Save time next time</p>
        <span className="text-[12.5px]">
          <ProfileLink>Open profile</ProfileLink>
        </span>
      </div>
      <p className="mt-0.5 text-[12.5px] text-ink-muted">
        Add these to your profile and questions like these fill themselves.
      </p>
      <ul className="mt-3 space-y-1.5">
        {tips.map((tip) => (
          <li key={tip} className="flex items-start gap-2 text-[13px] leading-relaxed text-ink-soft">
            <BulbIcon />
            <span className="min-w-0">{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Submitted / in progress
// ---------------------------------------------------------------------------
/** Submitted: the pill and one sentence on what went out. */
function OutcomeBanner({ application }: { application: ApplicationDetail }) {
  if (application.status !== 'submitted') return null
  const onNaukri = application.applyMode === 'easy_apply'
  const asked = application.screeningQuestions.length
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-card border border-success/25 bg-success/[0.06] px-4 py-3 sm:px-5">
      <ApplicationStatusPill status="submitted" />
      <p className="min-w-0 flex-1 text-[13px] leading-relaxed text-ink-soft">
        {onNaukri ? 'Applied with your Naukri profile. ' : `Submitted on ${application.job.companyName}'s site. `}
        {asked === 0
          ? 'This job didn’t ask any extra screening questions.'
          : `${asked} screening question${asked === 1 ? ' was' : 's were'} answered.`}
        {application.externalReference && (
          <>
            {' '}
            Reference <span className="num">{application.externalReference}</span>.
          </>
        )}
      </p>
      {application.submittedAt && (
        <span className="num w-full text-[12px] text-ink-muted sm:w-auto">{formatStamp(application.submittedAt)}</span>
      )}
    </div>
  )
}

function formatStamp(iso: string) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatClock(iso: string) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/**
 * Every event, oldest first: a mono time column, a dot, the message. The
 * latest dot pulses while the worker is still at it. CAPTCHA events
 * (`captcha_detected`, `captcha_waiting`, `captcha_solved`) read by their message.
 */
/** How many of the newest events the timeline shows before "Show all": a long run has hundreds. */
const TIMELINE_FOLD = 8

function Timeline({ application }: { application: ApplicationDetail }) {
  const start = application.events[0] ? new Date(application.events[0].createdAt).getTime() : 0
  const all = application.events.filter((event) => event.message)
  const working = WORKING.has(application.status)
  const [expanded, setExpanded] = useState(false)
  const folded = !expanded && all.length > TIMELINE_FOLD
  const events = folded ? all.slice(all.length - TIMELINE_FOLD) : all
  return (
    <section className="card overflow-hidden">
      <header className="flex items-baseline justify-between gap-3 border-b border-line px-4 py-3 sm:px-5">
        <h2 className="text-[14.5px] font-semibold text-ink">Application timeline</h2>
        {all[0] && <span className="num text-[12px] text-ink-faint">{formatStamp(all[0].createdAt)}</span>}
      </header>
      {folded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full border-b border-line px-4 py-2 text-left text-[12.5px] text-ink-muted hover:text-ink sm:px-5"
        >
          Show all {all.length} events ({all.length - TIMELINE_FOLD} earlier hidden)
        </button>
      )}
      <ol className="px-4 py-4 sm:px-5">
        {events.map((event, index) => {
          const offset = (new Date(event.createdAt).getTime() - start) / 1000
          const latest = index === events.length - 1
          const agent = event.eventType.startsWith('agent_')
          const diagnosis = agent && typeof event.payload?.diagnosis === 'string' ? event.payload.diagnosis : null
          const tone = agent
            ? 'bg-info ring-info/15'
            : event.toStatus === 'failed' || event.toStatus === 'job_expired'
              ? 'bg-danger ring-danger/15'
              : event.toStatus === 'needs_you'
                ? 'bg-warning ring-warning/15'
                : 'bg-success ring-success/15'
          return (
            <li key={event.id} className="relative grid grid-cols-[60px_11px_1fr] gap-x-3 pb-4 last:pb-0 sm:grid-cols-[64px_11px_1fr]">
              <p className="num whitespace-nowrap pt-px text-right text-[11px] leading-[18px] text-ink-faint sm:text-[11.5px]">
                <time dateTime={event.createdAt}>{formatClock(event.createdAt)}</time>
                {index > 0 && (
                  <span className="block text-[10.5px] text-ink-faint/80">
                    +{offset < 60 ? `${offset.toFixed(1)}s` : `${Math.round(offset / 60)}m`}
                  </span>
                )}
              </p>
              <span className="relative flex justify-center">
                {!(latest && !working) && <span aria-hidden="true" className="absolute top-[16px] -bottom-1 w-px bg-line" />}
                <span aria-hidden="true" className="relative mt-[4px] flex h-[11px] w-[11px]">
                  {latest && working && <span className={cn('absolute inline-flex h-full w-full animate-ping rounded-full opacity-60', tone)} />}
                  <span className={cn('relative inline-flex h-[11px] w-[11px] rounded-full ring-4', tone)} />
                </span>
              </span>
              <div className="min-w-0">
                <p className="text-[14px] leading-[18px] text-ink">{event.message}</p>
                {diagnosis && <p className="mt-0.5 text-[12px] leading-snug text-ink-muted">{diagnosis}</p>}
              </div>
            </li>
          )
        })}
        {working && (
          <li className="grid grid-cols-[60px_11px_1fr] gap-x-3 pt-1 text-[13px] text-ink-muted sm:grid-cols-[64px_11px_1fr]">
            <span />
            <span className="flex justify-center pt-0.5">
              <Spinner />
            </span>
            <span className="leading-[18px]">Working…</span>
          </li>
        )}
      </ol>
    </section>
  )
}

function ApplicationEmails({ application }: { application: ApplicationDetail }) {
  const emails = Array.isArray(application.emails) ? application.emails : []
  if (emails.length === 0 && !application.portalAccountEmail) return null
  return (
    <Disclosure
      title="Emails"
      count={emails.length}
      defaultOpen={emails.length > 0}
      meta={
        application.portalAccountEmail
          ? `${PORTAL_LABEL[application.portalType ?? ''] ?? 'Portal'} account: ${application.portalAccountEmail}`
          : undefined
      }
    >
      {application.portalAccountEmail && (
        <p className="truncate border-b border-line px-4 py-2 text-[12px] text-ink-faint sm:hidden">
          {PORTAL_LABEL[application.portalType ?? ''] ?? 'Portal'} account: {application.portalAccountEmail}
        </p>
      )}
      {emails.length === 0 ? (
        <p className="px-4 py-5 text-center text-[13px] text-ink-faint">No emails about this application yet.</p>
      ) : (
        <ul className="divide-y divide-line">
          {emails.map((email) => {
            const meta = categoryMeta(email.category)
            return (
              <li key={email.id}>
                <Link
                  to={`/dashboard/tracker?tab=inbox&message=${encodeURIComponent(email.id)}`}
                  className="flex min-h-[52px] items-center gap-3 px-4 py-2.5 transition-colors hover:bg-surface-muted sm:px-5"
                >
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', meta.dot)} title={meta.label} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] text-ink">{email.subject || '(no subject)'}</span>
                    <span className="block truncate text-[12px] text-ink-muted">
                      {meta.label}
                      {email.fromName ? ` · ${email.fromName}` : ''}
                    </span>
                  </span>
                  <span className="num shrink-0 text-[12px] text-ink-faint">{relativeTime(email.receivedAt)}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </Disclosure>
  )
}

function SentAnswers({ questions }: { questions: ScreeningQuestion[] }) {
  const answered = questions.filter((q) => q.answerValue)
  if (answered.length === 0) return null
  return (
    <Disclosure title="Answers sent" count={answered.length} defaultOpen>
      <dl className="divide-y divide-line">
        {answered.map((q) => (
          <div key={q.questionKey} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4 sm:px-5">
            <dt className="min-w-0 text-[13px] text-ink-muted">{q.questionText}</dt>
            <dd className="flex min-w-0 items-center gap-2 text-[14px] font-medium text-ink sm:shrink-0">
              <span className="min-w-0 break-words">
                {q.answerValue === 'true' ? 'Yes' : q.answerValue === 'false' ? 'No' : q.answerValue}
              </span>
              {q.answerSource && (
                <span
                  className={cn(
                    'shrink-0 rounded-pill px-2 py-0.5 text-[11px] font-medium',
                    q.answerSource === 'ai' ? 'bg-brand-soft text-brand' : 'bg-surface-muted text-ink-muted',
                  )}
                  title={q.rationale ?? undefined}
                >
                  {SOURCE_LABEL[q.answerSource]}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </Disclosure>
  )
}

// ---------------------------------------------------------------------------
// Test run ("rehearsal") report
// ---------------------------------------------------------------------------
const REHEARSAL_SOURCE: Record<RehearsalSource, string> = {
  ...SOURCE_LABEL,
  automatic: 'Auto',
  resume: 'Resume',
}

const ACCOUNT_GATE: Record<RehearsalAccountGate, string> = {
  none: 'No account needed',
  create_account: 'Asks you to create an account',
  sign_in: 'Asks you to sign in',
  verification: 'Asks for a code sent by email',
}

/**
 * What a test run saw on the employer's form: each step it reached (with a
 * screenshot), every field and what we put in it, what the portal complained
 * about, and where it stopped. Nothing in it was sent.
 */
function RehearsalPanel({ application, onApplied }: { application: ApplicationDetail; onApplied: () => void }) {
  const { requireApply } = useLicense()
  const reportQuery = useQuery({
    queryKey: ['application-rehearsal', application.id, application.updatedAt],
    queryFn: () => applicationApi.rehearsal(application.id),
    retry: false,
  })
  const [enlarged, setEnlarged] = useState<RehearsalStep | null>(null)
  const apply = useMutation({
    mutationFn: () => applicationApi.retry(application.id),
    onSuccess: () => {
      toast.info('Applying for real', `Submitting to ${application.job.companyName}`)
      onApplied()
    },
    onError: (error: ApiError) => {
      if (!isLicenseError(error)) toast.error('Could not apply', error.message)
    },
  })

  if (reportQuery.isLoading) {
    return (
      <section className="card flex items-center gap-2 px-4 py-4 text-[13px] text-ink-muted">
        <Spinner /> Loading the test run…
      </section>
    )
  }
  const report = reportQuery.data
  if (!report) return null

  const version = report.finishedAt ?? application.updatedAt
  const portal = report.portalLabel || PORTAL_LABEL[report.portal] || 'the employer’s site'
  const unanswered = new Set(report.unanswered.map((item) => item.key))
  const stopMessage = report.stop?.message || report.stop?.reason
  const canApply = application.isRehearsal && (application.status === 'failed' || application.needsReview)

  return (
    <>
      <Disclosure title="Test run" count={report.steps.length} meta={report.finishedAt ? formatStamp(report.finishedAt) : undefined} defaultOpen>
        <div className="space-y-4 px-4 py-4 sm:px-5">
          <dl className="grid grid-cols-1 gap-3 text-[13px] xs:grid-cols-3">
            <div>
              <dt className="text-ink-muted">Portal</dt>
              <dd className="font-medium text-ink">{portal}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">Steps reached</dt>
              <dd className="num font-medium text-ink">{report.steps.length}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">Account</dt>
              <dd className="font-medium text-ink" title={report.accountDetail || undefined}>
                {ACCOUNT_GATE[report.account] ?? report.account}
              </dd>
            </div>
          </dl>

          {stopMessage && (
            <p className="flex gap-2 rounded-field border border-info/25 bg-info/[0.06] px-3 py-2.5 text-[13px] leading-relaxed text-ink-soft">
              <InfoIcon />
              <span>
                {stopMessage}
                {report.stop?.step && <span className="text-ink-muted"> Last step: {report.stop.step}.</span>}
              </span>
            </p>
          )}

          {report.unanswered.length > 0 && (
            <p className="rounded-field border border-warning/30 bg-warning/[0.08] px-3 py-2.5 text-[13px] leading-relaxed text-ink-soft">
              ⚠ <span className="num">{report.unanswered.length}</span> required field{report.unanswered.length === 1 ? '' : 's'} had no answer:{' '}
              {report.unanswered.map((item) => item.label || item.key).join(', ')}.
            </p>
          )}

          {report.errors.length > 0 && (
            <div className="rounded-field border border-danger/25 bg-danger/[0.05] px-3 py-2.5 text-[13px] text-ink-soft">
              <p className="font-medium text-danger">The portal showed</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {report.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <ol className="space-y-3">
            {report.steps.map((step) => (
              <li key={step.index} className="panel overflow-hidden">
                <div className="flex items-start gap-3 px-3 py-3">
                  <RehearsalThumbnail
                    applicationId={application.id}
                    step={step}
                    version={version}
                    onOpen={() => setEnlarged(step)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-medium text-ink">
                      <span className="num">{step.index}.</span> {step.title || 'Step'}
                    </p>
                    <p className="mt-0.5 text-[12px] text-ink-muted">
                      <span className="num">{step.fields.length}</span> field{step.fields.length === 1 ? '' : 's'}
                      {step.stop && ' · stopped here'}
                    </p>
                    {step.errors.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-[12px] text-danger">
                        {step.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                {step.fields.length > 0 && (
                  <div className="overflow-x-auto border-t border-line">
                    <table className="w-full min-w-[460px] text-left text-[12.5px]">
                      <thead className="text-ink-muted">
                        <tr>
                          <th className="px-3 py-2 font-medium">Field</th>
                          <th className="px-3 py-2 font-medium">Our answer</th>
                          <th className="px-3 py-2 font-medium">Source</th>
                          <th className="px-3 py-2 font-medium">Required</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {step.fields.map((field) => {
                          const missing = field.required && !field.answer && unanswered.has(field.key)
                          return (
                            <tr key={field.key} className={cn(missing && 'bg-warning/[0.08]')}>
                              <td className="px-3 py-2 align-top text-ink-soft">
                                {field.label || field.key}
                                <span className="ml-1 text-[11px] text-ink-faint">{field.type}</span>
                              </td>
                              <td className="px-3 py-2 align-top text-ink">
                                {field.answer ? (
                                  <span className="break-words">{field.answer}</span>
                                ) : missing ? (
                                  <span className="font-medium text-warning">⚠ Unanswered</span>
                                ) : field.skipped ? (
                                  <span className="text-ink-faint">Not filled in a test run</span>
                                ) : (
                                  <span className="text-ink-faint">—</span>
                                )}
                                {field.fillError && (
                                  <span className="mt-0.5 block text-[11.5px] text-danger">Could not fill: {field.fillError}</span>
                                )}
                              </td>
                              <td className="px-3 py-2 align-top text-ink-muted">
                                {field.source ? REHEARSAL_SOURCE[field.source] ?? field.source : '—'}
                              </td>
                              <td className="px-3 py-2 align-top text-ink-muted">{field.required ? 'Yes' : 'No'}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </li>
            ))}
          </ol>

          {canApply && (
            <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
              <Button size="sm" onClick={() => requireApply(() => apply.mutate())} loading={apply.isPending}>
                Apply for real
              </Button>
              <span className="text-[12px] text-ink-muted">Runs the same steps and submits this time.</span>
            </div>
          )}
        </div>
      </Disclosure>

      <Modal
        open={enlarged !== null}
        onClose={() => setEnlarged(null)}
        title={enlarged ? `${enlarged.index}. ${enlarged.title || 'Step'}` : undefined}
        size="lg"
      >
        {enlarged && <RehearsalScreenshot applicationId={application.id} step={enlarged} version={version} />}
        {enlarged?.html && (
          <button
            type="button"
            onClick={() => applicationApi.downloadRehearsalFile(application.id, enlarged.html!)}
            className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-ink hover:underline"
          >
            <DownloadIcon /> Page HTML
          </button>
        )}
      </Modal>
    </>
  )
}

/** A captured screenshot, fetched with the session's token (an <img src> cannot send one). */
function useArtifactImage(
  keyPrefix: string,
  fetcher: (id: string, name: string) => Promise<Blob>,
  applicationId: string,
  name: string | null,
  version: string,
) {
  const query = useQuery({
    queryKey: [keyPrefix, applicationId, name, version],
    queryFn: () => fetcher(applicationId, name!),
    enabled: Boolean(name),
    staleTime: Infinity,
    retry: false,
  })
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!query.data) return
    const objectUrl = URL.createObjectURL(query.data)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [query.data])
  return { url, failed: query.isError }
}

const SHOT_STAGE: Record<string, string> = {
  submitted: 'Submitted',
  step: 'Step as filled',
  failed: 'Stopped here',
  needs_answers: 'Waiting on you',
  redirected: 'Sent to the employer’s site',
}

/** Where each run of this application ended, newest first: a strip on phones, a grid from sm. */
function RunScreenshots({ application }: { application: ApplicationDetail }) {
  const shots = [...(application.screenshots ?? [])].reverse()
  if (shots.length === 0) return null
  return (
    <Disclosure title="Screenshots" count={shots.length} defaultOpen>
      <div className="hscroll -mx-px snap-x gap-3 px-4 py-4 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-5 md:grid-cols-3">
        {shots.map((shot) => (
          <RunScreenshot key={shot.name} applicationId={application.id} shot={shot} />
        ))}
      </div>
    </Disclosure>
  )
}

function RunScreenshot({ applicationId, shot }: { applicationId: string; shot: ApplicationScreenshot }) {
  const { url, failed } = useArtifactImage(
    'application-screenshot', applicationApi.screenshot, applicationId, shot.name, shot.name,
  )
  const label = SHOT_STAGE[shot.stage ?? ''] ?? 'Screenshot'
  return (
    <figure className="panel w-[220px] shrink-0 snap-start overflow-hidden sm:w-auto">
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" title="Open full size" className="block">
          <img src={url} alt={`${label}: the page as the run left it`} className="h-36 w-full object-cover object-top sm:h-40" />
        </a>
      ) : (
        <div className="flex h-36 items-center justify-center text-[12px] text-ink-muted sm:h-40">
          {failed ? 'Not available' : <Spinner />}
        </div>
      )}
      <figcaption className="border-t border-line px-3 py-2 text-[12px] text-ink-muted">
        <span className="font-medium text-ink">{label}</span>
        {shot.capturedAt && <span className="num"> · {formatStamp(shot.capturedAt)}</span>}
      </figcaption>
    </figure>
  )
}

function useRehearsalImage(applicationId: string, name: string | null, version: string) {
  return useArtifactImage('application-rehearsal-file', applicationApi.rehearsalFile, applicationId, name, version)
}

function useAgentImage(applicationId: string, name: string | null, version: string) {
  return useArtifactImage('application-agent-file', applicationApi.agentTraceFile, applicationId, name, version)
}

function RehearsalThumbnail({
  applicationId,
  step,
  version,
  onOpen,
}: {
  applicationId: string
  step: RehearsalStep
  version: string
  onOpen: () => void
}) {
  const { url, failed } = useRehearsalImage(applicationId, step.screenshot, version)
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!step.screenshot}
      aria-label={`Enlarge the screenshot of ${step.title || `step ${step.index}`}`}
      className="h-[72px] w-[96px] shrink-0 overflow-hidden rounded-[8px] border border-line bg-surface-muted enabled:hover:border-line-strong"
    >
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover object-top" />
      ) : (
        <span className="grid h-full place-items-center text-[11px] text-ink-faint">
          {!step.screenshot || failed ? 'No image' : '…'}
        </span>
      )}
    </button>
  )
}

function RehearsalScreenshot({
  applicationId,
  step,
  version,
}: {
  applicationId: string
  step: RehearsalStep
  version: string
}) {
  const { url, failed } = useRehearsalImage(applicationId, step.screenshot, version)
  if (failed || !step.screenshot) return <p className="text-[13px] text-ink-muted">No screenshot for this step.</p>
  if (!url) return <p className="flex items-center gap-2 text-[13px] text-ink-muted"><Spinner /> Loading…</p>
  return (
    <div className="max-h-[70vh] overflow-auto rounded-field border border-line">
      <img src={url} alt={`Screenshot of ${step.title || `step ${step.index}`}`} className="block w-full" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live handoff: solve a CAPTCHA on the worker's own page
// ---------------------------------------------------------------------------
/**
 * While the worker runs, this watches for a parked page (a CAPTCHA). When one
 * appears it shows the live page; clicks (and short typing) are relayed onto
 * the real browser, and the worker continues by itself once the challenge
 * clears — the panel simply disappears. Full-bleed on phones so the page is
 * as large as the screen allows.
 */
function LiveSolvePanel({ application }: { application: ApplicationDetail }) {
  const id = application.id
  return (
    <LivePagePanel
      source={{
        key: ['application-live', id],
        status: () => applicationApi.live(id),
        screenshot: () => applicationApi.liveScreenshot(id),
        click: (x, y) => applicationApi.liveClick(id, x, y),
        type: (text) => applicationApi.liveType(id, text),
        scroll: (dy) => applicationApi.liveScroll(id, dy),
        reveal: () => applicationApi.liveReveal(id),
        done: () => applicationApi.liveDone(id),
      }}
      title="This site shows a CAPTCHA — solve it here"
      hint="Click directly on the page below; the application continues by itself."
      alt="The employer's page, live — click to interact"
      className="-mx-4 mb-4 border-y border-warning/30 bg-warning/[0.06] py-3 sm:mx-0 sm:rounded-card sm:border sm:px-4 sm:py-4"
      bleed
    />
  )
}

// ---------------------------------------------------------------------------
// Agent trace
// ---------------------------------------------------------------------------
const AGENT_MODE: Record<string, string> = {
  repair: 'Repaired the scripted run',
  navigator: 'Drove the site screen by screen',
}

/**
 * What the agent saw and did on the employer's site, turn by turn: a
 * screenshot of each screen, its diagnosis, and the moves it made (the
 * targets only — never the values that went into a field).
 */
function AgentTracePanel({ application }: { application: ApplicationDetail }) {
  const reportQuery = useQuery({
    queryKey: ['application-agent-trace', application.id, application.updatedAt],
    queryFn: () => applicationApi.agentTrace(application.id),
    retry: false,
  })
  const [enlarged, setEnlarged] = useState<AgentTurn | null>(null)

  if (reportQuery.isLoading) {
    return (
      <section className="card flex items-center gap-2 px-4 py-4 text-[13px] text-ink-muted">
        <Spinner /> Loading what the agent did…
      </section>
    )
  }
  const report = reportQuery.data
  if (!report || report.turns.length === 0) return null

  const version = report.finishedAt ?? application.updatedAt
  const portal = PORTAL_LABEL[report.portal] || 'the employer’s site'
  const patchReused = report.turns.some((turn) => turn.patchReused)

  return (
    <>
      <Disclosure title="Agent assistance" count={report.turns.length} meta={report.finishedAt ? formatStamp(report.finishedAt) : undefined}>
        <div className="space-y-4 px-4 py-4 sm:px-5">
          <dl className="grid grid-cols-1 gap-3 text-[13px] xs:grid-cols-3">
            <div>
              <dt className="text-ink-muted">What it did</dt>
              <dd className="font-medium text-ink">{AGENT_MODE[report.mode] ?? report.mode}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">Portal</dt>
              <dd className="font-medium text-ink">{portal}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">Screens</dt>
              <dd className="font-medium text-ink">
                <span className="num">{report.turns.length}</span>
                {patchReused && <span className="ml-1.5 text-[12px] font-normal text-ink-muted">· reused an earlier fix</span>}
              </dd>
            </div>
          </dl>

          {report.message && (
            <p className="flex gap-2 rounded-field border border-info/25 bg-info/[0.06] px-3 py-2.5 text-[13px] leading-relaxed text-ink-soft">
              <InfoIcon />
              <span>{report.message}</span>
            </p>
          )}

          <ol className="space-y-3">
            {report.turns.map((turn) => (
              <li key={turn.index} className="panel">
                <div className="flex items-start gap-3 px-3 py-3">
                  <AgentThumbnail
                    applicationId={application.id}
                    turn={turn}
                    version={version}
                    onOpen={() => setEnlarged(turn)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-medium text-ink">
                      <span className="num">{turn.index}.</span> {turn.title || 'Screen'}
                      {turn.phase && <span className="ml-1.5 text-[12px] font-normal text-ink-muted">{turn.phase.replace(/_/g, ' ')}</span>}
                    </p>
                    {turn.diagnosis && <p className="mt-0.5 text-[12.5px] leading-snug text-ink-soft">{turn.diagnosis}</p>}
                    {turn.actions.length > 0 && (
                      <ul className="mt-1.5 flex flex-wrap gap-1.5">
                        {turn.actions.map((action, index) => (
                          <li
                            key={index}
                            className="rounded-full border border-line bg-surface-muted px-2 py-0.5 text-[11.5px] text-ink-muted"
                            title={action.reason ?? undefined}
                          >
                            {action.target || action.kind}
                          </li>
                        ))}
                      </ul>
                    )}
                    {turn.actionErrors.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-[12px] text-danger">
                        {turn.actionErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    )}
                    {turn.patchReused && <p className="mt-1 text-[12px] text-ink-faint">Replayed the fix that worked here before.</p>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Disclosure>

      <Modal
        open={enlarged !== null}
        onClose={() => setEnlarged(null)}
        title={enlarged ? `${enlarged.index}. ${enlarged.title || 'Screen'}` : undefined}
        size="lg"
      >
        {enlarged && <AgentScreenshot applicationId={application.id} turn={enlarged} version={version} />}
        {enlarged?.html && (
          <button
            type="button"
            onClick={() => applicationApi.downloadAgentTraceFile(application.id, enlarged.html!)}
            className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-ink hover:underline"
          >
            <DownloadIcon /> Page HTML
          </button>
        )}
      </Modal>
    </>
  )
}

function AgentThumbnail({
  applicationId,
  turn,
  version,
  onOpen,
}: {
  applicationId: string
  turn: AgentTurn
  version: string
  onOpen: () => void
}) {
  const { url, failed } = useAgentImage(applicationId, turn.screenshot, version)
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!turn.screenshot}
      aria-label={`Enlarge the screenshot of ${turn.title || `screen ${turn.index}`}`}
      className="h-[72px] w-[96px] shrink-0 overflow-hidden rounded-[8px] border border-line bg-surface-muted enabled:hover:border-line-strong"
    >
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover object-top" />
      ) : (
        <span className="grid h-full place-items-center text-[11px] text-ink-faint">
          {!turn.screenshot || failed ? 'No image' : '…'}
        </span>
      )}
    </button>
  )
}

function AgentScreenshot({
  applicationId,
  turn,
  version,
}: {
  applicationId: string
  turn: AgentTurn
  version: string
}) {
  const { url, failed } = useAgentImage(applicationId, turn.screenshot, version)
  if (failed || !turn.screenshot) return <p className="text-[13px] text-ink-muted">No screenshot for this screen.</p>
  if (!url) return <p className="flex items-center gap-2 text-[13px] text-ink-muted"><Spinner /> Loading…</p>
  return (
    <div className="max-h-[70vh] overflow-auto rounded-field border border-line">
      <img src={url} alt={`Screenshot of ${turn.title || `screen ${turn.index}`}`} className="block w-full" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function BackIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M12.5 4.5 7 10l5.5 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ChevronLeftIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m10 4-4 4 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m6 4 4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M10.5 2.5 13.5 5.5 6 13H3v-3l7.5-7.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="m5 8.2 2 2L11 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function AlertIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="8" cy="11.2" r="0.8" fill="currentColor" />
    </svg>
  )
}

function InfoIcon() {
  return (
    <svg className="mt-0.5 h-[18px] w-[18px] shrink-0 text-warning" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 9v5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="10" cy="6.3" r="0.9" fill="currentColor" />
    </svg>
  )
}

function LockIcon() {
  return (
    <svg className="mt-0.5 h-3.5 w-3.5 shrink-0" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="2.5" y="6" width="9" height="6.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4.5 6V4.5a2.5 2.5 0 0 1 5 0V6" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}


function SendIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M14.5 1.5 7 9m7.5-7.5L10 14.5l-3-5.5-5.5-3L14.5 1.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  )
}



function TickIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="m3.5 7.2 2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path d="m3.5 5.5 3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function FileIcon() {
  return (
    <svg className="h-4 w-4 shrink-0 text-ink-muted" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 1.8h5.2L12.5 5v9.2H4V1.8Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9 2v3.2h3.3M6 8.5h4.5M6 11h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 2v7m0 0L4.2 6.2M7 9l2.8-2.8M2.5 11.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CopyIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="4.5" y="4.5" width="7.5" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M9.5 4.5V3.5A1.5 1.5 0 0 0 8 2H3.5A1.5 1.5 0 0 0 2 3.5V8a1.5 1.5 0 0 0 1.5 1.5h1" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}

function LetterIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="m3 5.5 7 5.5 7-5.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  )
}
